import { faBars } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useRef, useState } from 'react';
import Popup from 'reactjs-popup';
import { AdvancedFilter, CategoryFilterValues } from '../../../../../interfaces/interfaces';
import { fetchAndSetEvent } from '../../../../../reduxToolkit/slices/commonSlice';
import { useSelector, useDispatch } from '../../../../../reduxToolkit/typedRedux';
import FilterCategory from './FilterCategory';

interface BoothFiltersProps {
  fetchFromEvent?: boolean;
  onChange?: (filters: any) => void;
  description?: string;
  preselectedFilterValueId?: string;
}

const BoothFilters = (props: BoothFiltersProps) => {
  const { fetchFromEvent = true, onChange, description, preselectedFilterValueId } = props;

  const additionalCategories = useRef<string[]>([]);
  const filterValues = useRef<Record<string, boolean>>({});
  const filterParentValues = useRef<any>({});

  const event = useSelector((state) => state.common.event);
  const advancedFilters = useSelector((state) => state.common.advancedFilters);

  const [activeFilters, setActiveFilters] = useState<AdvancedFilter[]>(advancedFilters.filter((af) => af.isMainFilter));
  const [prevPreselectedFilter, setPrevPreselectedFilter] = useState<CategoryFilterValues | undefined>(undefined);
  const preselectedFilterValue = advancedFilters
    .map((af) => af.categoryFilterValues)
    .flat()
    .find((cfv) => cfv._id === preselectedFilterValueId);
  const dispatch = useDispatch();

  useEffect(() => {
    if (!advancedFilters || advancedFilters.length === 0) return;
    if (preselectedFilterValue) setFilterValue(preselectedFilterValue, true);
    else setActiveFilters(advancedFilters.filter((af) => af.isMainFilter));
    prevPreselectedFilter && setFilterValue(prevPreselectedFilter, false);
    setPrevPreselectedFilter(preselectedFilterValue);
  }, [preselectedFilterValue]);

  useEffect(() => {
    setActiveFilters(advancedFilters.filter((af) => af.isMainFilter));
  }, [advancedFilters]);

  function setFilterValue(cfv: CategoryFilterValues, checked: boolean) {
    if (checked) {
      // add categories in additionalCategories
      additionalCategories.current.push(...cfv.childCategoryFilter);
      //
    } else {
      // remove categories from additionalCategories
      cfv.childCategoryFilter.forEach((ccf) => additionalCategories.current.splice(additionalCategories.current.indexOf(ccf), 1));
      //
    }

    // removes duplicates from additional categories
    const acWithoutDuplicates = [...new Set(additionalCategories.current)];

    // filter those, or the one which mainFilter === true
    const additionalFilters = [
      ...advancedFilters.filter((af) => !acWithoutDuplicates.find((acwd) => acwd === af._id) && af.isMainFilter),
      ...advancedFilters.filter((af) => acWithoutDuplicates.find((acwd) => acwd === af._id) && !af.isMainFilter),
    ];

    // const sortedMainFirst = additionalFilters.sort((a, b) => (a.isMainFilter === b.isMainFilter ? 0 : a.isMainFilter ? -1 : 1));

    filterValues.current[cfv._id] = checked;

    if (!filterParentValues.current[cfv.parentCategoryFilter]) filterParentValues.current[cfv.parentCategoryFilter] = {};
    filterParentValues.current[cfv.parentCategoryFilter][cfv._id] = checked;
    console.log(filterParentValues);

    setActiveFilters(
      additionalFilters.filter((af) => (preselectedFilterValue ? !af.categoryFilterValues.find((cfv) => cfv._id === preselectedFilterValue?._id) : true))
    );
    submit();
  }

  async function submit() {
    /**
     * This is the new filtering approach
     *
     * We get an object of type:
     *
     * {
     *  "parentfilterid": {
     *    "childfilterid":true,
     *    "childfilterid2":false
     *  }
     *  "parentfilterid2": {
     *    "childfilterid3":true,
     *    "childfilterid4":false
     *  }
     * }
     *
     * and convert it to:
     *
     * {parentfilterid: [childfilterid], parentfilterid2:[childfilterid3]}
     *
     * This new filtering approach connects all parents via logical and, and the
     * child objects via logical or.
     */
    const filterCategoryValuesV2: any = {};
    Object.keys(filterParentValues.current).forEach((element) => {
      filterCategoryValuesV2[element] = Object.entries(filterParentValues.current[element])
        .filter((val) => {
          if (val[1] === true) return true;
          return false;
        })
        .map((val) => val[0]);
    });

    fetchFromEvent && dispatch(fetchAndSetEvent({ filterCategoryValuesV2, eventid: event?._id }));
    onChange && onChange(filterCategoryValuesV2);
  }

  if (activeFilters.length === 0) return <></>;

  return (
    <label className="relative mr-2">
      <Popup
        closeOnDocumentClick
        closeOnEscape
        position="right top"
        contentStyle={{ borderRadius: '1rem', overflow: 'hidden', padding: 0, border: 'none', width: 'auto', zIndex: 29 }}
        trigger={
          <button className="hover:shadow-2xl transition-shadow duration-200 w-full @sm:w-auto my-1 px-4 py-2 rounded-md focus:outline-none bg-buttons-main text-white pl-8 content-center templateBorder">
            <FontAwesomeIcon className="mr-1" icon={faBars} />
            {event?.template.individualNames.filter}
          </button>
        }>
        <div className="flex flex-row p-4 bg-white rounded">
          {description}
          <ul className="flex flex-col">
            {activeFilters
              .filter((af) => af.isMainFilter)
              .map(
                (category) =>
                  category.categoryFilterValues.length > 0 && (
                    <FilterCategory key={category._id} category={category} filterValues={filterValues} setFilterValue={setFilterValue} />
                  )
              )}
            <li />
          </ul>
          <ul className="grid grid-cols-3 gap-1">
            {activeFilters
              .filter((af) => !af.isMainFilter)
              .map(
                (category) =>
                  category.categoryFilterValues.length > 0 && (
                    <FilterCategory key={category._id} category={category} filterValues={filterValues} setFilterValue={setFilterValue} />
                  )
              )}
            <li />
          </ul>
        </div>
      </Popup>
    </label>
  );
};

export default BoothFilters;
